import { Instagram, Mail, MapPin, Phone, Plus, X } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { EditableText } from "@/components/EditableText";
import { Reveal } from "@/components/Reveal";

const Visit = () => {
  const { content, admin, addItem, removeItem } = useSite();
  return (
    <section id="visit" data-testid="visit-section" className="scroll-mt-16 px-6 py-28 md:px-12 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <Reveal>
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.3em] text-[#5C564D]">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0F47AF]" aria-hidden="true" />
            <span data-testid="visit-eyebrow">Find us</span>
          </div>
          <h2 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl">
            <EditableText path="visit.heading" testId="visit-heading" />
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-16 md:grid-cols-2 md:gap-24">
          <Reveal>
            <div>
              <div className="flex items-start gap-3">
                <MapPin className="mt-2 h-5 w-5 shrink-0 text-[#DA121A]" />
                <p className="font-serif text-3xl leading-snug tracking-tight md:text-4xl">
                  <EditableText path="visit.address1" testId="visit-address-1" className="block" />
                  <EditableText path="visit.address2" testId="visit-address-2" className="block italic text-[#5C564D]" />
                </p>
              </div>
              <ul className="mt-14 space-y-5 text-base md:text-lg">
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-[#5C564D]" />
                  <EditableText path="visit.phone" testId="visit-phone" />
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-[#5C564D]" />
                  <EditableText path="visit.email" testId="visit-email" />
                </li>
                <li className="flex items-center gap-3">
                  <Instagram className="h-4 w-4 shrink-0 text-[#5C564D]" />
                  <EditableText path="visit.instagram" testId="visit-instagram" />
                </li>
              </ul>
              {content.visit.photo && (
                <figure className="mt-14 max-w-sm" data-testid="visit-photo">
                  <div className="overflow-hidden border border-[#EADDCF]">
                    <img
                      src={content.visit.photo}
                      alt={content.visit.photo_caption || "Inside Kal's Cafe"}
                      className="h-full w-full object-cover"
                      data-testid="visit-photo-image"
                    />
                  </div>
                  <figcaption className="mt-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#5C564D]">
                    <span className="h-1 w-1 shrink-0 rounded-full bg-[#078930]" aria-hidden="true" />
                    <EditableText path="visit.photo_caption" testId="visit-photo-caption" />
                  </figcaption>
                  {admin && (
                    <EditableText
                      path="visit.photo"
                      testId="visit-photo-url"
                      className="mt-1 block w-full break-all text-[10px] normal-case tracking-normal text-[#5C564D]/60"
                    />
                  )}
                </figure>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div>
              <p
                className="mb-8 text-[11px] font-medium uppercase tracking-[0.3em] text-[#5C564D]"
                data-testid="visit-hours-label"
              >
                Opening hours
              </p>
              <ul className="space-y-5">
                {content.visit.hours.map((h, i) => (
                  <li key={i} className="flex items-baseline gap-4" data-testid={`visit-hours-row-${i}`}>
                    <EditableText path={`visit.hours.${i}.days`} testId={`visit-hours-days-${i}`} className="text-base md:text-lg" />
                    <span className="flex-1 border-b border-dotted border-[#1A1713]/25" aria-hidden="true" />
                    <EditableText
                      path={`visit.hours.${i}.time`}
                      testId={`visit-hours-time-${i}`}
                      className="font-serif text-xl italic md:text-2xl"
                    />
                    {admin && (
                      <button
                        data-testid={`visit-remove-hours-${i}`}
                        onClick={() => removeItem("visit.hours", i)}
                        className="text-[#DA121A]"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              {admin && (
                <button
                  data-testid="visit-add-hours"
                  onClick={() => addItem("visit.hours", { days: "New day", time: "00:00 — 00:00" })}
                  className="mt-6 flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#078930]"
                >
                  <Plus className="h-3 w-3" /> Add row
                </button>
              )}
              <p
                className="mt-14 border-l-2 border-[#FCDD09] pl-5 text-sm leading-relaxed text-[#5C564D]"
                data-testid="visit-note"
              >
                <EditableText path="visit.note" testId="visit-note-text" multiline />
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Visit;
